<template>
  <div class="packages-manager">
    <!-- Header avec actions -->
    <div class="manager-header">
      <div class="header-left">
        <h2 class="section-title">
          <i class="fas fa-box-open"></i>
          Gestion des Packages
        </h2>
        <p class="section-subtitle">Créez et gérez vos packages de produits</p>
      </div>
      <div class="header-actions">
        <button @click="$emit('refresh')" class="btn-refresh" :disabled="loading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          Actualiser
        </button>
        <button @click="$emit('create')" class="btn-primary">
          <i class="fas fa-plus"></i>
          Nouveau Package
        </button>
      </div>
    </div>

    <!-- Filtres et recherche -->
    <div class="filters-section">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Rechercher un package..."
          class="search-input"
        />
      </div>
      <div class="filter-controls">
        <select v-model="filterStatus" class="filter-select">
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>
    </div>

    <!-- Liste des packages -->
    <div class="packages-grid" v-if="!loading && filteredPackages.length > 0">
      <div 
        v-for="pkg in filteredPackages" 
        :key="pkg._id" 
        class="package-card"
      >
        <div class="package-header">
          <div class="package-title-section">
            <h3 class="package-name">
              <i class="fas fa-box-open"></i>
              {{ pkg.nom }}
            </h3>
            <span 
              class="package-status" 
              :class="pkg.actif ? 'status-active' : 'status-inactive'"
            >
              {{ pkg.actif ? 'Actif' : 'Inactif' }}
            </span>
          </div>
          <div class="package-actions">
            <button 
              @click="$emit('view', pkg)" 
              class="btn-icon btn-view"
              title="Voir les détails"
            >
              <i class="fas fa-eye"></i>
            </button>
            <button 
              @click="$emit('edit', pkg)" 
              class="btn-icon btn-edit"
              title="Modifier"
            >
              <i class="fas fa-edit"></i>
            </button>
            <button 
              @click="$emit('delete', pkg._id)" 
              class="btn-icon btn-delete"
              title="Supprimer"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <div class="package-body">
          <div class="package-info">
            <div class="info-item">
              <i class="fas fa-barcode"></i>
              <span class="info-label">Référence:</span>
              <span class="info-value">{{ pkg.reference }}</span>
            </div>
            <div class="info-item">
              <i class="fas fa-boxes"></i>
              <span class="info-label">Stock:</span>
              <span class="info-value stock-value" :class="getStockClass(pkg.quantiteStock)">
                {{ pkg.quantiteStock }} unités
              </span>
            </div>
            <div class="info-item">
              <i class="fas fa-euro-sign"></i>
              <span class="info-label">Prix:</span>
              <span class="info-value price-value">{{ formatPrice(pkg.prixUnitaire) }}</span>
            </div>
          </div>

          <div class="package-description" v-if="pkg.description">
            <p>{{ pkg.description }}</p>
          </div>

          <!-- Produits du package -->
          <div class="package-products">
            <div class="products-header">
              <h4>
                <i class="fas fa-cubes"></i>
                Produits inclus ({{ pkg.produits?.length || 0 }})
              </h4>
            </div>
            <div class="products-list" v-if="pkg.produits && pkg.produits.length > 0">
              <div 
                v-for="item in pkg.produits" 
                :key="getProductId(item)" 
                class="product-item"
              >
                <div class="product-info">
                  <i class="fas fa-cube"></i>
                  <span class="product-name">{{ getProductName(item) }}</span>
                </div>
                <div class="product-quantity">
                  <span class="quantity-badge">x{{ item.quantite }}</span>
                </div>
              </div>
            </div>
            <div v-else class="no-products">
              <i class="fas fa-inbox"></i>
              <p>Aucun produit dans ce package</p>
            </div>
          </div>
        </div>

        <div class="package-footer">
          <div class="footer-info">
            <span class="footer-date">
              <i class="fas fa-calendar"></i>
              Créé le {{ formatDate(pkg.dateCreation) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- État vide -->
    <div v-else-if="!loading && filteredPackages.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-box-open"></i>
      </div>
      <h3>Aucun package trouvé</h3>
      <p>Commencez par créer votre premier package de produits</p>
      <button @click="$emit('create')" class="btn-primary">
        <i class="fas fa-plus"></i>
        Créer un package
      </button>
    </div>

    <!-- Chargement -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement des packages...</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'PackagesManager',
  props: {
    packages: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'create', 'edit', 'delete', 'view'],
  setup(props) {
    const searchQuery = ref('')
    const filterStatus = ref('')

    const filteredPackages = computed(() => {
      let result = props.packages

      // Filtre par recherche
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(pkg => 
          pkg.nom?.toLowerCase().includes(query) ||
          pkg.reference?.toLowerCase().includes(query) ||
          pkg.description?.toLowerCase().includes(query)
        )
      }

      // Filtre par statut
      if (filterStatus.value) {
        result = result.filter(pkg => {
          if (filterStatus.value === 'active') return pkg.actif
          if (filterStatus.value === 'inactive') return !pkg.actif
          return true
        })
      }

      return result
    })

    const getStockClass = (stock) => {
      if (stock === 0) return 'stock-zero'
      if (stock < 10) return 'stock-low'
      return 'stock-ok'
    }

    const formatPrice = (price) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(price || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const getProductId = (item) => {
      if (typeof item.produit === 'object' && item.produit._id) {
        return item.produit._id
      }
      return item.produit || item._id || Math.random()
    }

    const getProductName = (item) => {
      if (typeof item.produit === 'object') {
        return item.produit.designation || item.produit.nom || 'Produit inconnu'
      }
      return 'Produit non chargé'
    }

    return {
      searchQuery,
      filterStatus,
      filteredPackages,
      getStockClass,
      formatPrice,
      formatDate,
      getProductId,
      getProductName
    }
  }
}
</script>

<style scoped>
.packages-manager {
  padding: 1.5rem;
}

.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
}

.header-left {
  flex: 1;
}

.section-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  color: var(--primary-blue);
  font-size: 1.5rem;
}

.section-subtitle {
  color: #6b7280;
  font-size: 0.95rem;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover:not(:disabled) {
  border-color: var(--primary-blue);
  color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.15);
}

.btn-refresh:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border: none;
  border-radius: 12px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

/* Filtres */
.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
}

.search-box {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 1rem;
  color: #9ca3af;
  font-size: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

.filter-select {
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-blue);
}

/* Grille des packages */
.packages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.package-card {
  background: white;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.3s ease;
}

.package-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
  border-color: var(--primary-blue);
}

.package-header {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  border-bottom: 2px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.package-title-section {
  flex: 1;
}

.package-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.package-name i {
  color: var(--primary-blue);
}

.package-status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.status-inactive {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.package-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-view {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.btn-view:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.3);
}

.btn-edit {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.btn-edit:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-delete {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.btn-delete:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.package-body {
  padding: 1.5rem;
}

.package-info {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.info-item i {
  color: var(--primary-blue);
  font-size: 1rem;
  width: 20px;
  text-align: center;
}

.info-label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.info-value {
  margin-left: auto;
  font-weight: 600;
  color: #1f2937;
}

.stock-value {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
}

.stock-ok {
  background: #d1fae5;
  color: #065f46;
}

.stock-low {
  background: #fed7aa;
  color: #92400e;
}

.stock-zero {
  background: #fee2e2;
  color: #991b1b;
}

.price-value {
  color: var(--primary-blue);
  font-size: 1.125rem;
}

.package-description {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.package-description p {
  margin: 0;
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.6;
}

.package-products {
  border-top: 2px solid #e5e7eb;
  padding-top: 1.5rem;
}

.products-header h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.products-header i {
  color: var(--primary-blue);
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.product-item:hover {
  background: #f3f4f6;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.product-info i {
  color: var(--primary-blue);
  font-size: 0.875rem;
}

.product-name {
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
}

.quantity-badge {
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.no-products {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.no-products i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.no-products p {
  margin: 0;
  font-size: 0.9rem;
}

.package-footer {
  padding: 1rem 1.5rem;
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
}

.footer-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.footer-date i {
  color: var(--primary-blue);
}

/* États vides et chargement */
.empty-state,
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #e5e7eb;
  border-top-color: var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  color: #6b7280;
  margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .packages-grid {
    grid-template-columns: 1fr;
  }

  .manager-header {
    flex-direction: column;
    gap: 1rem;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .btn-refresh,
  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .filters-section {
    flex-direction: column;
  }
}
</style>
